import { useMemo, useRef } from "react";

interface Alert {
  id: string;
  message: string;
  [key: string]: unknown;
}

/**
 * Returns a referentially stable alerts array that only changes
 * when the alert content actually differs.
 */
export function useStableAlerts<T extends Alert>(alerts: T[]): T[] {
  const prevRef = useRef<T[]>(alerts);

  return useMemo(() => {
    if (alerts === prevRef.current) return prevRef.current;
    // Shallow content comparison by id + message (the fields that can change via refresh)
    const changed =
      alerts.length !== prevRef.current.length ||
      alerts.some((a, i) => {
        const prev = prevRef.current[i];
        return a.id !== prev?.id || a.message !== prev?.message;
      });
    if (changed) {
      prevRef.current = alerts;
    }
    return prevRef.current;
  }, [alerts]);
}
